import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Instagram } from "lucide-react";
import { GALLERY, LINKS } from "@/content/site";
import { MaskLines, Reveal } from "@/components/site/motion";

export const StoryTeaser = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const clip = useTransform(scrollYProgress, [0, 0.4], ["inset(12% 10% 12% 10% round 2.5rem)", "inset(0% 0% 0% 0% round 2.5rem)"]);
  return (
    <section ref={ref} data-testid="story-teaser" className="bg-[#8b6cf2] py-24 text-[#fff5e8] sm:py-32">
      <div className="wrap grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <motion.div style={{ clipPath: clip }} className="relative aspect-[4/5] overflow-hidden">
            <motion.img src="/img/lineup-flatlay.webp" alt="A rainbow lineup of TeaDM drinks" style={{ y }} className="absolute inset-0 -top-[12%] h-[124%] w-full object-cover" loading="lazy" />
          </motion.div>
        </div>
        <div className="lg:col-span-6">
          <p className="eyebrow">Our story</p>
          <MaskLines lines={["Drinks that", "bring people", "together."]} className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
          <Reveal delay={0.2}>
            <p className="font-serif-x mt-8 text-2xl leading-snug sm:text-3xl">“Tea DM began with a simple goal: to create a space where everyone can enjoy high-quality beverages in a warm and inviting atmosphere.”</p>
            <Link to="/our-story" data-testid="story-teaser-cta" className="btn btn-cream mt-10">Read our story <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const SPANS = [
  "col-span-2 row-span-2 lg:col-start-1 lg:row-start-1",
  "lg:col-start-3 lg:row-start-1",
  "lg:col-start-4 lg:row-start-1 lg:row-span-2",
  "lg:col-start-3 lg:row-start-2",
  "lg:col-start-1 lg:row-start-3 lg:row-span-2",
  "lg:col-start-2 lg:row-start-3 lg:col-span-2",
  "lg:col-start-4 lg:row-start-3",
  "lg:col-start-2 lg:row-start-4 lg:col-span-2",
  "lg:col-start-4 lg:row-start-4",
];

export const Gallery = () => (
  <section data-testid="gallery-section" className="bg-[#fff5e8] py-24 sm:py-32">
    <div className="wrap">
      <div className="mb-12 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow text-[#ff2e63]">@teadmeastcoast</p>
          <MaskLines lines={["Caught on", "camera."]} className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
        </div>
        <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="gallery-instagram-follow-btn" className="btn btn-berry self-start sm:self-auto"><Instagram className="h-4 w-4" />Follow on Instagram</a>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:grid-rows-[repeat(4,220px)]">
        {GALLERY.map((g, i) => (
          <a key={g.src} href={LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid={`gallery-tile-${i}`} className={`group relative aspect-square overflow-hidden rounded-[1.6rem] bg-[#1a0a10]/5 lg:aspect-auto ${SPANS[i]}`}>
            <img src={g.src} alt={g.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110" />
            <span className="absolute inset-0 grid place-items-center bg-[#ff2e63]/0 opacity-0 transition-[opacity,background-color] duration-500 group-hover:bg-[#ff2e63]/35 group-hover:opacity-100"><Instagram className="h-8 w-8 text-white" /></span>
          </a>
        ))}
      </div>
    </div>
  </section>
);
